import { FieldValues, UseFormReturn, Path } from 'react-hook-form';
import AutoComplete, { AutoCompleteItem } from './AutoComplete';
import { FormField, FormControl, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Suspense } from 'react';
import Spinner from '../spinner/Spinner';
import { Await } from 'react-router';
import { AxiosResponse } from 'axios';

export type RHFAutoCompleteProps<T extends FieldValues, K> = {
	control: UseFormReturn<T>['control'];
	name: Path<T>;
	label: string;
	placeholder: string;
	items: K[] | Promise<AxiosResponse<K[]>>;
	isMultiSelect?: boolean;
	closeOnSelect?: boolean;
	isLoading?: boolean;
	onSearch?: (search: string) => void;
	onSelect?: (selected: AutoCompleteItem) => void;
	itemToOption: (item: K) => AutoCompleteItem;
	isDisabled?: boolean;
};

export const RHFAutoComplete = <T extends FieldValues, K>(props: RHFAutoCompleteProps<T, K>) => {
	const {
		control,
		name,
		label,
		placeholder,
		items,
		isMultiSelect = false,
		isLoading,
		closeOnSelect = false,
		onSearch,
		onSelect,
		itemToOption,
		isDisabled = false
	} = props;

	const isDataAsync = items instanceof Promise;

	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => {
				const renderAutoComplete = (resolvedItems: K[]) => {
					const options = resolvedItems.map(itemToOption);
					const selectedItems: AutoCompleteItem[] = field.value ?? [];

					const removeSelectedItem = (option: AutoCompleteItem) => {
						return selectedItems.filter(i => i.value !== option.value);
					};

					return (
						<AutoComplete
							isDisabled={isDisabled}
							TriggerWrapper={FormControl}
							isMultiSelect={isMultiSelect}
							placeholder={placeholder}
							closeOnSelect={closeOnSelect}
							items={options}
							selectedItems={selectedItems}
							onSelect={option => {
								const exists = selectedItems.find(i => i.value === option.value);
								onSelect?.(option);

								if (exists) {
									field.onChange(removeSelectedItem(option));
								} else {
									if (!isMultiSelect) {
										field.onChange([option]);
									} else {
										field.onChange([...selectedItems, option]);
									}
								}
							}}
							onRemoveItem={option => {
								field.onChange(removeSelectedItem(option));
							}}
							onClear={() => {
								field.onChange([]);
							}}
							onSearch={onSearch}
							loading={isLoading}
						/>
					);
				};

				return (
					<FormItem>
						<FormLabel>{label}</FormLabel>

						{isDataAsync ? (
							<Suspense fallback={<Spinner />}>
								<Await resolve={items}>{response => renderAutoComplete(response.data)}</Await>
							</Suspense>
						) : (
							renderAutoComplete(items)
						)}

						<FormMessage />
					</FormItem>
				);
			}}
		/>
	);
};
